import { useState, useCallback } from 'react';
import { Share2, Trash2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { useCalendarSharing } from '../hooks/useCalendarSharing';
import type { CalendarShareVisibility, CalendarUserSearchResult } from '../types';

interface ShareCalendarDialogProps {
  open: boolean;
  onClose: () => void;
  calendarId: string | null;
  calendarName?: string;
}

const VISIBILITY_LABELS: Record<CalendarShareVisibility, string> = {
  'free-busy': 'Free/busy only',
  titles: 'Titles and times',
  full: 'Full details',
};

function truncateHolderId(id: string): string {
  if (id.length <= 14) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

export function ShareCalendarDialog({
  open,
  onClose,
  calendarId,
  calendarName,
}: ShareCalendarDialogProps) {
  const confirm = useConfirm();
  const { shares, loading, createShare, updateShare, deleteShare } = useCalendarSharing({
    calendarId: open ? calendarId : null,
  });

  const [username, setUsername] = useState('');
  const [visibility, setVisibility] = useState<CalendarShareVisibility>('free-busy');
  const [searchResult, setSearchResult] = useState<CalendarUserSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!username.trim()) return;
    setSearching(true);
    try {
      const { user } = await api.searchCalendarUsers(username.trim());
      setSearchResult(user);
      if (!user) {
        toast({ title: 'User not found', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Search failed', variant: 'destructive' });
    } finally {
      setSearching(false);
    }
  }, [username]);

  const handleShare = useCallback(async () => {
    if (!searchResult) return;
    setCreating(true);
    try {
      await createShare({
        granteeHolderId: searchResult.personalHolderId,
        granteeUsername: searchResult.username,
        visibility,
      });
      toast({ title: `Shared with @${searchResult.username}` });
      setUsername('');
      setSearchResult(null);
      setVisibility('free-busy');
    } catch (err) {
      toast({
        title: 'Failed to share',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  }, [searchResult, visibility, createShare]);

  const handleUpdateVisibility = useCallback(
    async (consentId: string, newVisibility: CalendarShareVisibility) => {
      try {
        await updateShare(consentId, { visibility: newVisibility });
      } catch (err) {
        toast({
          title: 'Failed to update',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      }
    },
    [updateShare],
  );

  const handleRevoke = useCallback(
    async (consentId: string, granteeUsername: string | undefined, isGlobal: boolean) => {
      // Global grants give access to ALL the granter's calendars.
      // Revoking from the per-calendar dialog removes that broader
      // access too — confirm so the user can't lose it by accident.
      if (isGlobal) {
        const who = granteeUsername ? `@${granteeUsername}` : 'this user';
        const ok = await confirm({
          title: `Revoke ${who}'s access to every calendar?`,
          description: `${who} has access to ALL your calendars (not just this one). Revoking here removes their access to every calendar.`,
          confirmLabel: 'Revoke access',
          destructive: true,
        });
        if (!ok) return;
      }
      try {
        await deleteShare(consentId);
        toast({ title: `Revoked access for @${granteeUsername || 'user'}` });
      } catch (err) {
        toast({
          title: 'Failed to revoke',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      }
    },
    [deleteShare, confirm],
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            Share {calendarName || 'Calendar'}
          </DialogTitle>
        </DialogHeader>

        {/* Add share form */}
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="share-username" className="sr-only">
                Username
              </Label>
              <Input
                id="share-username"
                placeholder="Enter exact username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setSearchResult(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSearch}
              disabled={searching || !username.trim()}
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Find'}
            </Button>
          </div>

          {searchResult && (
            <div className="rounded border p-3 space-y-3">
              <div className="text-sm">
                Found: <span className="font-medium">@{searchResult.username}</span>
              </div>

              <div className="flex items-center gap-3">
                <Select
                  value={visibility}
                  onValueChange={(v) => setVisibility(v as CalendarShareVisibility)}
                >
                  <SelectTrigger className="w-44 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(VISIBILITY_LABELS) as CalendarShareVisibility[]).map((v) => (
                      <SelectItem key={v} value={v}>
                        {VISIBILITY_LABELS[v]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button size="sm" onClick={handleShare} disabled={creating}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Share
              </Button>
            </div>
          )}
        </div>

        {/* Existing shares */}
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : shares.length > 0 ? (
          <div className="space-y-2 mt-2">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Shared with
            </div>
            {shares.map((share) => (
              <div key={share.consentId} className="flex items-center gap-2 text-sm">
                <span
                  className="flex-1 truncate flex items-center gap-1.5"
                  title={share.granteeHolderId}
                >
                  <span className="truncate">
                    {share.granteeUsername
                      ? `@${share.granteeUsername}`
                      : truncateHolderId(share.granteeHolderId)}
                  </span>
                  {share.isGlobal && (
                    <span
                      className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                      title="This share grants access to all your calendars"
                    >
                      All calendars
                    </span>
                  )}
                </span>
                <Select
                  value={share.visibility}
                  onValueChange={(v) =>
                    handleUpdateVisibility(share.consentId, v as CalendarShareVisibility)
                  }
                >
                  <SelectTrigger className="w-44 h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(VISIBILITY_LABELS) as CalendarShareVisibility[]).map((v) => (
                      <SelectItem key={v} value={v}>
                        {VISIBILITY_LABELS[v]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive"
                  onClick={() =>
                    handleRevoke(share.consentId, share.granteeUsername, share.isGlobal)
                  }
                  title={share.isGlobal ? 'Revoke (access to ALL your calendars)' : 'Revoke access'}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-2">
            Not shared with anyone yet
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
